<script setup lang="ts">
import { cn } from "@/lib/utils";
import { eventBus } from "#bb/lib/eventBus";
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
import {
  authoringPathBasename,
  normalizeCandidateReportCachePath,
  resolveCandidateReportCachePath,
  resolveSystemReportCachePath,
  toAbsoluteSitePath,
} from "#bb/lib/pathContract";

const logger = useLogger("ReportProviderRaw");
const props = defineProps<{ id: string }>();
const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id));
const features = inject("render-features", { editor: false });

// Exposed commands (same edit flow)
const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Raw Report Provider editors",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit this raw report provider",
        action: async () => {
          (eventBus as any).emit(`component:onEdit:${props.id}`, { data: definitionData.value });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Report Provider (Raw)",
    icon: "lucide:box-select",
  },
});

const route = useRoute();
const {
  reportData,
  fetchReports,
  reportFileName: activeReportFileName,
  setReportFileName,
} = useReportsStore(props.id);

// Reactive report state (mirrors original provider structure)
type ReportKind = "users" | "user" | "constructs" | "raw";
const report = reactive<{
  data: any[];
  kind: ReportKind;
  context?: any;
  loading: boolean;
  initialized: boolean;
  debug: boolean;
  fileName: string;
  cachePath: string;
}>({
  data: [],
  kind: "raw",
  loading: false,
  initialized: false,
  debug: false,
  fileName: "report",
  cachePath: "/data/system/cache/report",
});
provide("report", report);
provide("reportRefresh", () => refreshLiveData());

// Full live report file (all candidates), cached once the editor requests a
// live-data refresh so field discovery + preview use real data, not samples.
const liveUsers = ref<any[] | null>(null);

// Raw users published per provider (keyed by block id) via Nuxt global state.
// The field-picker editors (ranked-explorer etc.) render in a separate subtree
// — the resource-editor panel — so they can't inject this provider's `report`.
// They resolve their provider ancestor and read this channel instead, giving
// robust field discovery from the same underlying data regardless of source
// (live file, sample, or an authoritative refresh).
const fieldSourceUsers = useState<any[] | null>(`report-fieldsource:${props.id}`, () => null);

// Optional report-user override (e.g. the in-chat assessment report) so a report can resolve a
// specific candidate without relying on the page route's `?user=`. Normal report pages inject
// nothing and keep using the route query.
const injectedReportUserId = inject("reportUserId", null) as { value?: string } | null;

async function runCustomMapper(rawData: any, context?: any) {
  const defaultMapper = "function mapper(data, context){return data}";
  const mapperCode = definitionData.value?.customMapper || defaultMapper;
  try {
    const fn = new Function(
      "data",
      "context",
      `${mapperCode}; return mapper(data, context)`
    );
    const result = fn(rawData, context);
    return await Promise.resolve(result);
  } catch (e) {
    logger.error("Failed to execute custom mapper", e);
    return rawData;
  }
}

function pickDefaultDataKey(hasUserId: boolean): string {
  const configured = definitionData.value?.dataKey;
  if (typeof configured === "string" && configured.length) return configured;
  return hasUserId ? "constructs" : "users";
}

function configuredCandidateReportPath(): string {
  const configured = definitionData.value?.reportFileName;
  return normalizeCandidateReportCachePath(
    typeof configured === "string" ? configured : undefined,
  );
}

function syncReportTarget(userId?: string): {
  cachePath: string;
  fileName: string;
} {
  const candidatePath = configuredCandidateReportPath();
  const fileApiPath = userId
    ? resolveCandidateReportCachePath(candidatePath, userId)
    : resolveSystemReportCachePath(candidatePath);
  const resolvedFileName = authoringPathBasename(fileApiPath) || "report";
  const cachePath = toAbsoluteSitePath(fileApiPath);

  if (report.fileName !== resolvedFileName) report.fileName = resolvedFileName;
  if (report.cachePath !== cachePath) report.cachePath = cachePath;
  if (activeReportFileName.value !== resolvedFileName) setReportFileName(resolvedFileName);
  report.debug = Boolean(definitionData.value?.debug);
  return { cachePath, fileName: resolvedFileName };
}

/** Project a users array into report.data/kind/context via the custom mapper. */
async function applyFromUsers(users: any[], hasUserId: boolean) {
  const user = users[0] ?? null;
  const dataKey = pickDefaultDataKey(hasUserId);

  let rawData: any;
  let kind: ReportKind;

  if (dataKey === "users") {
    rawData = users;
    kind = "users";
  } else if (dataKey === "*") {
    rawData = hasUserId ? user : users;
    kind = hasUserId ? "user" : "users";
  } else {
    rawData = hasUserId ? (user?.[dataKey] ?? []) : users;
    kind = hasUserId ? "constructs" : "users";
  }

  const selected = definitionData.value?.selectedConstructIds || [];
  if (kind === "constructs" && Array.isArray(selected) && selected.length && Array.isArray(rawData)) {
    rawData = rawData.filter((construct: any) => selected.includes(String(construct.id)));
  }

  const context = {
    user,
    users,
    config: definitionData.value,
    hasUserId,
    dataKey,
    debug: report.debug,
    reportFileName: report.fileName,
    reportCachePath: report.cachePath,
    rawUsers: users,
    rawUser: user,
  };

  report.kind = kind;
  report.context = context;
  report.data = await runCustomMapper(rawData, context);

  // Publish the raw users so out-of-tree field-picker editors can discover
  // fields from the real data (not the mapper's — possibly reshaped — output).
  fieldSourceUsers.value = users;
}

async function load() {
  report.initialized = true;
  report.loading = true;
  try {
    const userId = injectedReportUserId?.value ?? (route.query.user as string | undefined);
    const reportTarget = syncReportTarget(features.editor ? undefined : userId);

    if (features.editor) {
      // A live-data refresh (triggered from the provider editor) takes precedence
      // so field discovery + preview reflect the real report file, not sample data.
      if (liveUsers.value) {
        await applyFromUsers(liveUsers.value, false);
        return;
      }

      const sample = definitionData.value?.sampleData;
      if (sample) {
        try {
          const parsed = JSON.parse(sample);
          const users = Array.isArray(parsed) ? parsed : [parsed].filter(Boolean);
          const hasUserId = !!(users[0]?.userId ?? users[0]?.id);
          await applyFromUsers(users, hasUserId);
          return;
        } catch (e) {
          logger.error("Invalid sample JSON; falling back to static sample", e);
        }
      }

      // No cached live data and no sample: pull the real report file so field
      // discovery + preview reflect actual data by default. The report API always
      // serves the current system-cache aggregate; the provider editor's "Load live
      // data" button just forces a fresh reload that bypasses the client cache.
      try {
        await fetchReports(undefined, {
          fileName: reportTarget.fileName,
          throwOnError: true,
        });
        const users = Array.isArray(reportData.value) ? reportData.value : [];
        if (users.length) {
          liveUsers.value = users;
          await applyFromUsers(users, false);
          return;
        }
      } catch (e) {
        logger.warn("Editor live-data auto-load failed; using static skeleton", e);
      }

      // Nothing available yet (new/empty report) — render a minimal skeleton so
      // the block stays visible while editing.
      const rawData = [
        { id: "comm", name: "Communication", score: 0.45 },
        { id: "lead", name: "Leadership", score: 0.35 },
        { id: "prob", name: "Problem Solving", score: 0.75 },
        { id: "team", name: "Teamwork", score: 0.25 },
      ];
      const context = {
        user: null,
        users: [],
        config: definitionData.value,
        hasUserId: false,
        dataKey: "constructs",
        debug: report.debug,
        reportFileName: report.fileName,
        reportCachePath: report.cachePath,
        rawUsers: [],
        rawUser: null,
      };
      report.kind = "constructs";
      report.context = context;
      report.data = await runCustomMapper(rawData, context);
      fieldSourceUsers.value = [];
      return;
    }

    const users = await fetchReports(userId, {
      fileName: reportTarget.fileName,
      throwOnError: true,
    });

    // Rapid `?user=` navigation fires overlapping load() calls. If the target
    // user changed while this request was in flight, a newer load() owns the
    // result — bail so a stale response can't clobber the current user's data.
    const currentUserId = injectedReportUserId?.value ?? (route.query.user as string | undefined);
    if (currentUserId !== userId) return;

    await applyFromUsers(users, !!userId);
  } catch (e) {
    logger.error("Failed to load raw report data", e);
  } finally {
    report.loading = false;
  }
}

/**
 * Force-reload the complete live report file (all candidates) from the
 * system-cache aggregate that the report API serves, bypassing the client-side
 * cache. Triggered by the provider editor's "Load live data" button and by any
 * child report's refresh control, so field discovery + preview run against
 * real-world data and the ranked list reflects the current published aggregate.
 */
async function refreshLiveData(): Promise<{ ok: boolean; users: number; rows: number; error?: string }> {
  report.initialized = true;
  report.loading = true;
  try {
    const reportTarget = syncReportTarget();
    const users = await fetchReports(undefined, {
      fileName: reportTarget.fileName,
      refresh: true,
      throwOnError: true,
    });
    liveUsers.value = users;
    await applyFromUsers(users, false);
    return {
      ok: true,
      users: users.length,
      rows: Array.isArray(report.data) ? report.data.length : 0,
    };
  } catch (e: any) {
    logger.error("Failed to load live report data", e);
    return { ok: false, users: 0, rows: 0, error: e?.message ?? String(e) };
  } finally {
    report.loading = false;
  }
}

// The provider editor renders in a separate component subtree (the resource-
// editor panel) and can't reach `refreshLiveData` through provide/inject.
// Bridge the two over the global event bus, keyed by block id: the editor emits
// a refresh request, we run the authoritative live-data load, then emit the
// result back so the editor can report progress + outcome.
const refreshRequestKey = `component:onRefreshLiveData:${props.id}`;
const refreshResultKey = `component:onLiveDataRefreshed:${props.id}`;

async function handleExternalRefresh() {
  const result = await refreshLiveData();
  (eventBus as any).emit(refreshResultKey, result);
}

onMounted(() => {
  (eventBus as any).on(refreshRequestKey, handleExternalRefresh);
  void load();
});
onUnmounted(() => {
  (eventBus as any).off(refreshRequestKey, handleExternalRefresh);
});
watch(() => [route.query.user, injectedReportUserId?.value], load);
</script>

<template>
  <loading-indicator
    v-if="report.loading || !report.initialized"
    role="status"
    aria-live="polite"
  />
  <div v-else :class="cn($attrs.class ?? '')">
    <slot />
  </div>
  <div v-if="features.editor" class="hidden">
    <editor-reports-provider :id="props.id" />
  </div>
</template>

<script lang="ts">
export default {
  editor: () => import("#bb/components/editor/reports/provider.vue"),
  componentId: ComponentDefinitionIds.REPORT_PROVIDER_RAW,
  id: "reportprovider-raw",
  icon: "lucide:box-select",
  type: "Report Provider Raw",
  description: "Provides untouched API response data to child report components.",
  default: {
    columnsSettings: { default: 1 },
    dataKey: "users", // force users to avoid fallback remapping
    sampleData: "",
    selectedConstructIds: [],
    reportFileName: "report",
    debug: false,
    children: [],
  },
  categories: ["Reports"],
  order: 12005,
};
</script>
